import { defineStore } from "pinia";
import { computed, ref } from "vue";

import { captureMessage } from "@/error/sentry";
import { asReadonly } from "@/utils/vue";

const itemTypes = [
  "checkAuth",
  "login",
  "user",
  "featureToggles",
  "connect",
  "initBackend",
  "setSession",
  "links",
  "almTypes",
  "stickerTypes",
  "stickyTypes",
  "session",
  "boards",
] as const;

type LoadingItem = (typeof itemTypes)[number];

export const useLoadingStore = defineStore("loading", () => {
  const items = { started: new Array<LoadingItem>(), loading: new Array<LoadingItem>() };
  const started = ref(0);
  const finished = ref(0);
  const ended = ref(false);

  const progress = computed(() => ({
    started: started.value / itemTypes.length,
    finished: finished.value / itemTypes.length,
  }));

  function start() {
    items.started = [];
    items.loading = [];
    started.value = 0;
    finished.value = 0;
  }

  function end({ expectAllItems = true } = {}) {
    ended.value = true;
    if (!expectAllItems) return;
    if (started.value !== itemTypes.length || finished.value !== itemTypes.length) {
      // TODO avoid sentry spam, remove when REN-13052 is fixed
      if (multipleSetSession()) {
        return;
      }
      captureMessage("loading items count not reached", {
        counts: {
          total: itemTypes.length,
          items,
          started: started.value,
          finished: finished.value,
        },
      });
    }
  }

  function multipleSetSession() {
    const setSessionCount = count("setSession");
    return (
      started.value === finished.value &&
      setSessionCount <= 3 &&
      setSessionCount - 1 === started.value - itemTypes.length
    );
  }

  function count(type: LoadingItem) {
    return items.started.filter((item) => item === type).length;
  }

  function load<T>(item: LoadingItem, action: Promise<T>): Promise<T> {
    if (ended.value) {
      return action;
    }
    started.value++;
    items.started.push(item);
    items.loading.push(item);
    return action.finally(() => {
      finished.value++;
      items.loading.splice(
        items.loading.findIndex((i) => i === item),
        1,
      );
    });
  }

  return { ended: asReadonly(ended), progress, start, end, load };
});
